<template>
  <fullscreen-dialog
    v-model="model"
    :title="receiptTitle"
    icon="mdi-truck-check"
    color="primary"
    hide-footer
    :loading="loading"
  >
    <template v-slot:app-bar-actions>
      <print-button @click="print" />
    </template>

    <v-form ref="form" v-model="valid">
      <div class="receipt-layout">
        <div class="receipt-top-row">
          <warehouse-receipt-info-card
            :edited-item="editedItem"
            :warehouses="warehouses"
            :loading-warehouses="loadingWarehouses"
            :groceries="groceries"
            :document-transactions="documentTransactions"
            :dispatcher-number.sync="dispatcherNumber"
            :delivery-date.sync="deliveryDate"
          />
          <warehouse-receipt-summary-card
            :edited-item="editedItem"
            :groceries="groceries"
            :document-transactions="documentTransactions"
            :valid="valid"
            :loading="loading"
            @finish="save"
          />
        </div>

        <warehouse-receipt-items-table
          :groceries="groceries"
          :document-transactions="documentTransactions"
          :document-type="editedItem.document_type"
        />

        <v-card outlined>
          <v-card-title class="pb-0">{{ $t("entry_note") }}</v-card-title>
          <v-divider />
          <v-card-text>
            <text-area v-model="note" hide-details color="white" />
          </v-card-text>
        </v-card>
      </div>
    </v-form>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasValidation from "@/mixins/hasValidation";
import hasWarehouseUsers from "@/mixins/warehouse/hasWarehouseUsers";
import hasReceiptCalculations from "@/mixins/warehouse/hasReceiptCalculations";
import hasReceiptPrinting from "@/mixins/warehouse/hasReceiptPrinting";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import WarehouseReceiptInfoCard from "./receipt/WarehouseReceiptInfoCard.vue";
import WarehouseReceiptSummaryCard from "./receipt/WarehouseReceiptSummaryCard.vue";
import WarehouseReceiptItemsTable from "./receipt/WarehouseReceiptItemsTable.vue";
import { orderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehousePurchaseReceiptDialog",
  mixins: [
    hasModel,
    hasValidation,
    hasSnackbar,
    hasPrinting,
    hasWarehouseUsers,
    hasReceiptCalculations,
    hasReceiptPrinting,
  ],
  components: {
    FullscreenDialog,
    WarehouseReceiptInfoCard,
    WarehouseReceiptSummaryCard,
    WarehouseReceiptItemsTable,
  },
  props: {
    editedItem: {
      type: Object,
      default: orderDocumentGrocery,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      note: "",
      dispatcherNumber: "",
      deliveryDate: null,
      warehouses: [],
      loadingWarehouses: false,
    };
  },
  computed: {
    groceries() {
      return this.editedItem.groceries || [];
    },
    documentTransactions() {
      return (this.editedItem.children || []).flatMap((child) =>
        (child.transactions || []).filter((t) => t.transaction_type === "entry")
      );
    },
    receivedById() {
      return this.getSelectedUserId(this.editedItem.received_by);
    },
    receiptTitle() {
      return `${this.$t("receipt_title")} #${this.editedItem?.id || ""}`.trim();
    },
  },
  methods: {
    async fetchWarehouses() {
      if (this.loadingWarehouses) return;
      this.loadingWarehouses = true;
      try {
        const response = await warehouseService.getWarehouses();
        this.warehouses = response.data || [];
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_warehouses"));
      } finally {
        this.loadingWarehouses = false;
      }
    },

    initializeReceiptDefaults() {
      this.groceries.forEach((grocery) => {
        const remaining = this.getRemaining(grocery);
        if (
          (grocery.order === null ||
            grocery.order === undefined ||
            grocery.order === "") &&
          remaining > 0
        ) {
          this.$set(grocery, "order", remaining);
        }
        if (
          (grocery.gross_quantity === null ||
            grocery.gross_quantity === undefined ||
            grocery.gross_quantity === "") &&
          Number(grocery.order) > 0
        ) {
          this.$set(grocery, "gross_quantity", Number(grocery.order));
        }
        if (
          !Number(grocery.unit_price) &&
          Number(grocery.total_amount) > 0 &&
          Number(grocery.amount) > 0
        ) {
          this.$set(
            grocery,
            "unit_price",
            Number(grocery.total_amount) / Number(grocery.amount)
          );
        }
      });
    },
    getSelectedUserId(user) {
      if (!user) return null;
      return typeof user === "object" ? user.id : user;
    },
    getSelectedUserName(user) {
      if (!user) return "";
      if (typeof user === "object") {
        return user.fullName || [user.name, user.lastname].filter(Boolean).join(" ");
      }
      return (this.users || []).find((u) => u.id === user)?.fullName || "";
    },
    getReceiptGroceries() {
      return this.groceries
        .map((item) => ({
          grocery_id: item.id,
          warehouse_id: this.editedItem.warehouse_id,
          amount: Number(item.order) || 0,
          measure: item.measure,
          transaction_type: "entry",
          notes: this.note,
          code: item.code,
          name: item.name,
        }))
        .filter((item) => item.amount > 0);
    },
    getNextStatus() {
      const hasAnyDelivered = this.groceries.some(
        (g) => this.getGroceryDelivered(g) + this.getCurrentReceiptAmount(g) > 0
      );
      if (!hasAnyDelivered) return null;
      const allDelivered = this.groceries.every(
        (g) => this.getRemaining(g) - this.getCurrentReceiptAmount(g) <= 0
      );
      return allDelivered ? "delivered" : "delivered-half";
    },
    async save() {
      const isValid = this.$refs.form ? this.$refs.form.validate() : true;
      const groceries = this.getReceiptGroceries();

      if (
        !isValid ||
        !groceries.length ||
        !this.receivedById ||
        !this.editedItem.warehouse_id
      ) {
        return;
      }

      this.loading = true;

      const params = {
        ...this.editedItem,
        document_type: "entry",
        transaction_type: "entry",
        parent_id: this.editedItem.id,
        warehouse_id: this.editedItem.warehouse_id,
        received_by: this.receivedById,
        delivery_date: this.deliveryDate,
        shipping_date: this.deliveryDate,
        document_number: this.dispatcherNumber,
        supplier_invoice_number: this.dispatcherNumber,
        notes: this.note,
        groceries,
      };

      try {
        await this.handleRequest(warehouseService.addDocumentTransaction(params), {
          successMessage: this.$t("warehouse_entry_created"),
          errorMessagePrefix: this.$t("error_creating_warehouse_entry") + ": ",
        });

        const status = this.getNextStatus();
        if (status) {
          try {
            await warehouseService.patchWarehouse(this.editedItem.id, {
              data: { status },
            });
            this.showSuccessSnackbar(this.$t("warehouse_status_updated"));
          } catch (error) {
            this.showErrorSnackbar(this.$t("error_updating_warehouse_status"));
          }
        }

        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Failed to save invoice", error);
      } finally {
        this.loading = false;
      }
    },
    print() {
      this.printReceipt();
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.fetchUsers();
        this.fetchWarehouses();
        this.deliveryDate =
          this.editedItem.delivery_date || this.editedItem.document_date || null;
        this.dispatcherNumber = this.editedItem.supplier_invoice_number || "";
        this.note = this.editedItem.notes || "";
        this.initializeReceiptDefaults();
      }
    },
  },
};
</script>

<style scoped>
.receipt-layout {
  display: grid;
  gap: 16px;
  min-height: calc(100vh - 156px);
}

.receipt-top-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 332px;
  gap: 16px;
}

@media (max-width: 1264px) {
  .receipt-top-row {
    grid-template-columns: 1fr;
  }
}
</style>
